/**
 * manualContent.js
 * Default content for the SoldiKeeper User Manual.
 * This is the single source of truth — fully editable via ManualEditor.js.
 * Images reference /assets/manual/{filename}.jpg
 */

const defaultManualContent = {
  meta: {
    title: 'SoldiKeeper User Manual',
    subtitle: 'Your complete guide to mastering personal finance with AI',
    version: '2.7.0',
    lastUpdated: 'September 2026',
  },
  chapters: [
    {
      id: 'ch1',
      title: 'Introduction to SoldiKeeper',
      sections: [
        {
          id: 'ch1-s1',
          title: 'What is SoldiKeeper?',
          type: 'body',
          body: 'SoldiKeeper is an AI-powered personal finance platform that combines intelligent budgeting, expense tracking, receipt scanning, bill splitting, and financial insights in one unified application. Designed for individuals and families across Europe, SoldiKeeper helps you understand where your money goes and gives you AI-driven guidance to improve your financial health.',
          image: { src: '/assets/manual/d01_landing_hero.jpg', caption: 'SoldiKeeper landing page — your journey starts here' },
        },
        {
          id: 'ch1-s2',
          title: 'Key Features at a Glance',
          type: 'list',
          items: [
            'Smart Budgeting with AI recommendations',
            'Transaction tracking across 20+ categories',
            'Receipt scanning with OCR technology',
            'SplitSmart bill splitting with friends and groups',
            'Subscription management and renewal alerts',
            'AI-powered financial insights and natural language chat',
            'Recurring transaction templates for automation',
            'Multi-language support (English, German, French, Spanish, Italian, Portuguese)',
            'Mobile app for Android (iOS coming soon)',
          ],
          image: { src: '/assets/manual/d02_landing_features.jpg', caption: 'Feature overview on the SoldiKeeper website' },
        },
        {
          id: 'ch1-s3',
          title: 'System Requirements',
          type: 'body',
          body: 'Web Browser: Chrome 90+, Firefox 88+, Safari 14+, Edge 90+. Mobile: Android 5.1+ (Google Play Store). Internet connection required for all features. Progressive Web App (PWA) available for offline access to recent data.',
        },
      ],
    },
    {
      id: 'ch2',
      title: 'Getting Started — Account Setup',
      sections: [
        {
          id: 'ch2-s1',
          title: 'Creating Your Account',
          type: 'steps',
          steps: [
            'Navigate to www.soldikeeper.com in your web browser',
            'Click "Sign Up" in the top-right navigation bar',
            'Enter your email address and choose a strong password (minimum 8 characters)',
            'Click "Create Account" to proceed',
            'Check your email inbox for a verification link from SoldiKeeper',
            'Click the verification link within 24 hours to activate your account',
            'You will be redirected to your personal dashboard',
          ],
          image: { src: '/assets/manual/03_register_page.jpg', caption: 'Account registration page' },
          tip: 'Use a strong, unique password. SoldiKeeper stores your data with bank-grade encryption.',
        },
        {
          id: 'ch2-s2',
          title: 'Signing In',
          type: 'steps',
          steps: [
            'Go to www.soldikeeper.com',
            'Click "Sign In" in the top-right navigation',
            'Enter your registered email address',
            'Enter your password',
            'Click "Sign In" to access your dashboard',
            'Tick "Remember me" to stay logged in on this device',
          ],
          image: { src: '/assets/manual/02_login_page.jpg', caption: 'SoldiKeeper sign-in page' },
        },
        {
          id: 'ch2-s3',
          title: 'Forgot Password Recovery',
          type: 'steps',
          steps: [
            'Click "Forgot Password?" on the login page',
            'Enter your registered email address',
            'Click "Send Reset Link"',
            'Check your inbox for the password reset email (check spam/junk folder too)',
            'Click the reset link within 1 hour of receiving it',
            'Enter your new password and confirm it',
            'Click "Reset Password" — you can now sign in with the new credentials',
          ],
          image: { src: '/assets/manual/04_forgot_password.jpg', caption: 'Password recovery page' },
          tip: 'If you do not receive the email within 5 minutes, check your spam folder or contact support.',
        },
        {
          id: 'ch2-s4',
          title: 'Choosing a Subscription Plan',
          type: 'body',
          body: 'SoldiKeeper offers 5 pricing tiers. Free: Basic tracking, 3 budgets, 50 transactions/month. Standard (€4.99/month): Unlimited transactions, 10 budgets, receipt scanning. Premium (€9.99/month): All Standard + AI insights, SplitSmart, recurring templates. Family (€14.99/month): Up to 5 accounts, shared budgets, family analytics. Business (€29.99/month): Unlimited everything, priority support, business reports. All paid plans include a 60-day free trial.',
          image: { src: '/assets/manual/05_pricing_plans.jpg', caption: 'Pricing plans — pick the plan that fits your needs' },
          tip: 'Start with the 60-day free trial on Premium to explore all AI features before committing to a plan.',
        },
      ],
    },
    {
      id: 'ch3',
      title: 'Navigating the Dashboard',
      sections: [
        {
          id: 'ch3-s1',
          title: 'Dashboard Overview',
          type: 'body',
          body: 'The Dashboard is your financial command center. It provides a real-time snapshot of your financial health including total balance, monthly spending trends, budget progress bars, recent transaction activity, and AI-generated personalized insights.',
          image: { src: '/assets/manual/11_phone_dashboard.jpg', caption: 'SoldiKeeper dashboard — financial overview at a glance' },
        },
        {
          id: 'ch3-s2',
          title: 'Dashboard Sections',
          type: 'list',
          items: [
            'Financial Summary: Total balance, income vs. expenses for the current month with trend arrows',
            'Budget Progress: Visual bars showing spending vs. limits for each active budget (color-coded green/yellow/red)',
            'Recent Activity: Your last 10 transactions with amounts, categories, and dates',
            'AI Insights Panel: Personalized financial tips, anomaly alerts, and savings opportunities',
            'Quick Actions: Floating buttons to add transactions, scan receipts, or split a bill',
          ],
        },
        {
          id: 'ch3-s3',
          title: 'Desktop Navigation',
          type: 'body',
          body: 'On desktop and tablet, use the left sidebar to navigate between sections: Dashboard, Transactions, Budgets, Receipts, SplitSmart, Subscriptions, Recurring Templates, AI Insights, Help, and Settings. Click any icon to navigate instantly.',
          image: { src: '/assets/manual/17_tablet_view1.jpg', caption: 'Desktop/tablet navigation sidebar' },
        },
        {
          id: 'ch3-s4',
          title: 'Mobile Navigation',
          type: 'body',
          body: 'On mobile, use the bottom navigation bar with 5 tabs: Dashboard, Transactions, Budgets, Receipts, and More (for SplitSmart, AI Insights, Settings). Tap the "More" tab to access all remaining sections. Swipe left or right on list items to reveal quick action buttons.',
          image: { src: '/assets/manual/m01_mobile_landing_hero.jpg', caption: 'Mobile bottom navigation bar' },
        },
      ],
    },
    {
      id: 'ch4',
      title: 'Managing Transactions',
      sections: [
        {
          id: 'ch4-s1',
          title: 'Adding a Transaction',
          type: 'steps',
          steps: [
            'Click "Transactions" in the navigation menu',
            'Click the "+" button or "Add Transaction"',
            'Select transaction type: Expense or Income',
            'Enter the amount (e.g., 45.50)',
            'Choose a category from the dropdown (e.g., Food & Dining, Transport)',
            'Add a description or merchant name (e.g., "Carrefour grocery run")',
            'Select the date — defaults to today',
            'Click "Save Transaction"',
          ],
          image: { src: '/assets/manual/12_phone_transactions.jpg', caption: 'Transaction list with add button' },
        },
        {
          id: 'ch4-s2',
          title: 'Editing or Deleting a Transaction',
          type: 'body',
          body: 'Edit: Click the pencil (edit) icon on any transaction row. Modify any field — amount, category, description, or date — then click Save. Delete: Click the trash icon on any transaction row and confirm in the dialog box. Note: deletion is permanent and cannot be undone.',
          warning: 'Deleting a transaction will also remove it from any budget calculations. Budget totals will update immediately.',
        },
        {
          id: 'ch4-s3',
          title: 'Filtering and Searching',
          type: 'body',
          body: 'Use the filter panel above the transaction list to narrow results by: date range (last 7/30/90 days or custom), category, amount range (min/max), and transaction type (expense/income). Use the search bar to find transactions by description or merchant name. Active filters appear as removable chips above the list.',
        },
        {
          id: 'ch4-s4',
          title: 'Transaction Categories',
          type: 'list',
          items: [
            'Food & Dining, Groceries, Coffee & Bars, Restaurants',
            'Transport, Fuel, Public Transport, Parking, Taxi/Rideshare',
            'Housing, Rent, Utilities, Home Maintenance, Internet/Phone',
            'Healthcare, Pharmacy, Gym & Fitness, Medical Services',
            'Entertainment, Streaming, Games, Cinema, Concerts',
            'Shopping, Clothing, Electronics, Household Items',
            'Education, Books, Courses, Tuition',
            'Travel, Hotels, Flights, Vacation Expenses',
            'Personal Care, Beauty, Haircuts',
            'Savings, Investments, Insurance Premiums',
            'Income, Salary, Freelance, Rental Income, Gifts',
            'Other / Miscellaneous',
          ],
        },
      ],
    },
    {
      id: 'ch5',
      title: 'Budgets and Spending Limits',
      sections: [
        {
          id: 'ch5-s1',
          title: 'Creating a Budget',
          type: 'steps',
          steps: [
            'Click "Budgets" in the navigation menu',
            'Click "Create Budget" or the "+" button',
            'Enter a budget name (e.g., "Monthly Groceries", "Entertainment Apr")',
            'Set the spending limit amount (e.g., 400)',
            'Choose the category this budget tracks',
            'Select the budget period: Monthly, Weekly, or Custom',
            'For Custom: set start and end dates',
            'Toggle "Enable Rollover" if you want unspent amounts to carry forward',
            'Click "Save Budget"',
          ],
          image: { src: '/assets/manual/13_phone_budgets.jpg', caption: 'Budget creation and progress view' },
        },
        {
          id: 'ch5-s2',
          title: 'Understanding Budget Progress',
          type: 'body',
          body: 'Each budget displays a progress bar showing spent vs. limit. Colors indicate status: Green (0–60% spent) — on track. Yellow (60–80% spent) — approaching limit. Red (80%+ spent) — at risk. A notification is sent when you reach 80% of any budget.',
          tip: 'Click any budget to see a breakdown of all transactions that count toward it.',
        },
        {
          id: 'ch5-s3',
          title: 'Budget Rollover',
          type: 'body',
          body: 'When rollover is enabled, any unspent amount at the end of a period is added as bonus credit to the next period. Example: if your €400 grocery budget has €80 left at month end, next month starts with €480. The rolled-over amount appears in green as an addition to your base limit.',
          note: 'Rollover only carries unused amounts forward — it does not carry overages back.',
        },
        {
          id: 'ch5-s4',
          title: 'AI Budget Recommendations',
          type: 'body',
          body: 'When creating or editing a budget, look for the "🤖 AI Suggests" badge. The AI analyzes your last 3 months of spending in that category and recommends a realistic limit. Click the suggested amount to auto-fill it. These recommendations update monthly as your spending patterns evolve.',
        },
      ],
    },
    {
      id: 'ch6',
      title: 'Receipt Scanning with OCR',
      sections: [
        {
          id: 'ch6-s1',
          title: 'Scanning a Receipt',
          type: 'steps',
          steps: [
            'Click "Receipts" in the navigation menu',
            'Click "Scan Receipt" or the camera icon',
            'On mobile: point your camera at the receipt and tap the capture button',
            'On web: click "Upload Photo" and select a JPG, PNG, or PDF file',
            'Wait 5–15 seconds for AI extraction to complete',
            'Review the extracted data: merchant name, date, total, and line items',
            'Edit any incorrect fields directly in the form',
            'Click "Save Receipt" to store it',
            'Optionally click "Create Transaction from Receipt" to auto-create a transaction entry',
          ],
          image: { src: '/assets/manual/14_phone_receipts.jpg', caption: 'Receipt scanning and management screen' },
        },
        {
          id: 'ch6-s2',
          title: 'Managing Saved Receipts',
          type: 'body',
          body: 'All scanned receipts appear in the Receipts list with thumbnail previews. Use the search bar to find receipts by merchant name or date. Click any receipt to view the original image alongside extracted data. You can edit data, attach the receipt to an existing transaction, download the image, or delete it.',
        },
        {
          id: 'ch6-s3',
          title: 'Tips for Best Scan Quality',
          type: 'list',
          items: [
            'Good lighting is critical — avoid shadows falling across the receipt',
            'Keep the receipt flat on a contrasting background (dark receipt → light surface)',
            'Capture the entire receipt including top and bottom edges',
            'For very long receipts, fold and scan in sections',
            'Digital receipts (PDF email attachments) give near-perfect accuracy',
            'The AI handles wrinkled, slightly blurry, or faded receipts well',
          ],
          tip: 'PDF receipts from online purchases produce the most accurate extraction results.',
        },
      ],
    },
    {
      id: 'ch7',
      title: 'SplitSmart — Bill Splitting',
      sections: [
        {
          id: 'ch7-s1',
          title: 'What is SplitSmart?',
          type: 'body',
          body: 'SplitSmart is SoldiKeeper\'s intelligent group expense management feature. Create groups for trips, flatmates, or events. Add shared expenses, track who paid what, and let the AI calculate minimum-transaction settlements that clear all debts with the fewest possible payments.',
          image: { src: '/assets/manual/15_phone_splitsmart.jpg', caption: 'SplitSmart group expense overview' },
        },
        {
          id: 'ch7-s2',
          title: 'Creating a Group',
          type: 'steps',
          steps: [
            'Click "SplitSmart" in the navigation menu',
            'Click "New Group"',
            'Enter a group name (e.g., "Rome Weekend", "Flat April 2026")',
            'Add members by entering their names (or email if they are SoldiKeeper users)',
            'Set the group currency if different from your default',
            'Click "Create Group"',
          ],
        },
        {
          id: 'ch7-s3',
          title: 'Adding a Shared Expense',
          type: 'steps',
          steps: [
            'Open the group',
            'Click "Add Expense"',
            'Enter the expense description (e.g., "Dinner at Ristorante Belli")',
            'Enter the total amount',
            'Select who paid (the person who put money down)',
            'Choose split method: Equally, By percentage, or Custom amounts',
            'Select which group members are included in this expense',
            'Click "Add Expense"',
          ],
          tip: 'Use "Custom amounts" for expenses where some people ordered more expensive items.',
        },
        {
          id: 'ch7-s4',
          title: 'Settling Debts',
          type: 'body',
          body: 'Click "Settle Up" to see the AI-optimized settlement plan. Instead of everyone paying everyone else, SplitSmart calculates the minimum number of transactions to clear all balances. Example: in a group of 5, instead of 10 separate payments, only 4 may be needed. Mark each payment as "Settled" when done — balances update in real time.',
        },
      ],
    },
    {
      id: 'ch8',
      title: 'Subscription Manager',
      sections: [
        {
          id: 'ch8-s1',
          title: 'Tracking Your Subscriptions',
          type: 'body',
          body: 'The Subscription Manager gives you a complete view of all recurring payments — streaming services, software licenses, gym memberships, news sites, and more. It alerts you before renewals so you can cancel services you no longer use.',
        },
        {
          id: 'ch8-s2',
          title: 'Adding a Subscription',
          type: 'steps',
          steps: [
            'Click "Subscriptions" in the navigation menu',
            'Click "Add Subscription"',
            'Enter the service name (e.g., "Netflix", "Adobe Creative Cloud")',
            'Enter the cost — monthly or annual',
            'Set the billing cycle: Monthly or Yearly',
            'Enter the next renewal date',
            'Select a category: Entertainment, Software, Health, News, Other',
            'Click "Save"',
          ],
        },
        {
          id: 'ch8-s3',
          title: 'Renewal Alerts',
          type: 'body',
          body: 'SoldiKeeper sends you an in-app notification and (optionally) an email 3 days before any subscription renews. This gives you time to cancel if you no longer use the service. View all upcoming renewals in the "Upcoming" tab sorted by date.',
          note: 'Your total monthly subscription cost is shown as a summary card at the top of the Subscriptions page.',
        },
      ],
    },
    {
      id: 'ch9',
      title: 'Recurring Transactions',
      sections: [
        {
          id: 'ch9-s1',
          title: 'What are Recurring Templates?',
          type: 'body',
          body: 'Recurring transaction templates automate the logging of predictable regular expenses and incomes — monthly rent, weekly grocery budget, salary credits, or annual insurance premiums. Once set up, SoldiKeeper creates the transactions automatically on schedule.',
        },
        {
          id: 'ch9-s2',
          title: 'Creating a Recurring Template',
          type: 'steps',
          steps: [
            'Click "Recurring" in the navigation menu',
            'Click "New Template"',
            'Enter the transaction description (e.g., "Monthly Rent")',
            'Enter the amount',
            'Select the category',
            'Choose the transaction type: Expense or Income',
            'Set the frequency: Daily, Weekly, Monthly, or Yearly',
            'Set the start date and an optional end date',
            'Toggle "Auto-create" ON to add transactions automatically, or OFF to receive a reminder to add them manually',
            'Click "Save Template"',
          ],
          tip: 'Use recurring templates for salary, rent, and fixed subscriptions to keep your transaction history complete without manual effort.',
        },
      ],
    },
    {
      id: 'ch10',
      title: 'AI Financial Insights',
      sections: [
        {
          id: 'ch10-s1',
          title: 'The AI Insights Engine',
          type: 'body',
          body: 'SoldiKeeper deploys 12 specialized AI agents that analyze your financial data collaboratively. These agents identify spending patterns, detect unusual transactions, predict upcoming expenses, benchmark your categories against typical users, and surface actionable savings opportunities — all personalized to your specific financial situation.',
          image: { src: '/assets/manual/16_phone_insights.jpg', caption: 'AI Insights — personalized financial intelligence' },
        },
        {
          id: 'ch10-s2',
          title: 'Viewing Your Insights',
          type: 'list',
          items: [
            'Spending Pattern Analysis: How your spending changed month over month',
            'Budget Performance Report: Which budgets you crushed and which ran over',
            'Savings Opportunities: Specific areas where you could save money',
            'Anomaly Alerts: Unusual or duplicate transactions flagged automatically',
            'Predictive Cash Flow: Projected balance for the next 30 days',
            'Category Benchmarks: How your spending compares to similar users',
          ],
        },
        {
          id: 'ch10-s3',
          title: 'AI Financial Chat',
          type: 'body',
          body: 'The AI Chat lets you ask questions about your finances in plain language. The AI has full access to your transaction history, budgets, and insights. Example questions: "How much did I spend on restaurants last month?", "Am I on track with my grocery budget?", "What were my top 3 spending categories this quarter?", "How much did I save compared to last month?"',
          tip: 'The more transactions you have, the smarter and more accurate the AI insights become. Aim for at least 1 month of data before relying on AI recommendations.',
        },
        {
          id: 'ch10-s4',
          title: 'Generating an Insights Report',
          type: 'steps',
          steps: [
            'Navigate to "AI Insights"',
            'Click "Generate Report"',
            'Select the time period: Last Month, Last 3 Months, Last Year, or Custom',
            'Click "Generate"',
            'Wait 10–30 seconds for the AI to process and analyze your data',
            'View the full report in-app or download it as a PDF',
          ],
        },
      ],
    },
    {
      id: 'ch11',
      title: 'Settings and Profile',
      sections: [
        {
          id: 'ch11-s1',
          title: 'Account Settings Overview',
          type: 'body',
          body: 'Access Settings by clicking your avatar in the top-right corner and selecting "Settings". From here manage: your display name and email, password change, preferred currency (EUR, USD, GBP, CHF, and 20+ more), interface language (6 languages), notification preferences, and connected services.',
        },
        {
          id: 'ch11-s2',
          title: 'Changing Your Language',
          type: 'steps',
          steps: [
            'Click your avatar/name in the top-right corner',
            'Select "Settings"',
            'Scroll to the "Preferences" section',
            'Click the "Language" dropdown',
            'Select your preferred language from the list',
            'The app immediately switches — no page reload needed',
          ],
          note: 'Language can also be changed from the language flag selector visible in the top navigation bar.',
        },
        {
          id: 'ch11-s3',
          title: 'Managing Your Subscription',
          type: 'body',
          body: 'Go to Settings > Subscription to view your current plan, next billing date, and payment method. Click "Change Plan" to upgrade or downgrade — changes take effect at the next billing cycle. Click "Cancel Subscription" to stop future renewals; you retain access until the end of the current paid period.',
        },
        {
          id: 'ch11-s4',
          title: 'Deleting Your Account',
          type: 'body',
          body: 'Settings > Account > Delete Account. Read the warning carefully — all your financial data, transactions, budgets, and receipts will be permanently and irreversibly erased. Enter your password to confirm identity. Click "Delete Account". This cannot be undone.',
          warning: 'Account deletion is permanent. Export your data first if you may need it in the future. There is no recovery option.',
          image: { src: '/assets/manual/d12_delete_account.jpg', caption: 'Account deletion confirmation page' },
        },
      ],
    },
    {
      id: 'ch12',
      title: 'Mobile App',
      sections: [
        {
          id: 'ch12-s1',
          title: 'Installing the Mobile App',
          type: 'body',
          body: 'Android: Download SoldiKeeper from the Google Play Store — search "SoldiKeeper" or scan the QR code on our website. iOS: Coming soon to the Apple App Store. Progressive Web App (PWA): On any mobile browser, open www.soldikeeper.com, tap the browser menu, and select "Add to Home Screen" for an app-like experience.',
          image: { src: '/assets/manual/m05_mobile_landing_full.jpg', caption: 'SoldiKeeper mobile app experience' },
        },
        {
          id: 'ch12-s2',
          title: 'Mobile-Specific Features',
          type: 'list',
          items: [
            'Camera receipt scanning using your phone camera — no file upload needed',
            'Touch-optimized interface with large tap targets and swipe gestures',
            'Push notifications for budget alerts and subscription renewals',
            'Biometric login (fingerprint/face recognition) for quick secure access',
            'Swipe-left on transactions to reveal quick edit and delete actions',
            'Share expenses from other apps directly to SplitSmart via the Share Sheet',
          ],
        },
        {
          id: 'ch12-s3',
          title: 'Mobile vs Web Differences',
          type: 'body',
          body: 'All core features are available on both web and mobile. The mobile app additionally offers: camera access for receipt scanning, push notifications, biometric authentication, and offline data viewing. The web app offers a wider layout better suited for reviewing charts and detailed reports.',
          image: { src: '/assets/manual/19_tablet_10in_view1.jpg', caption: 'SoldiKeeper on tablet — ideal for detailed financial review' },
        },
      ],
    },
    {
      id: 'ch13',
      title: 'SoldiDMS Overview',
      sections: [
        {
          id: 'ch13-s1',
          title: 'What SoldiDMS Is (and Is Not)',
          type: 'body',
          body: `SoldiDMS is SoldiKeeper's document system — vaults, versions, approvals, permissions, audit. It is not Google Drive with a logo. Files start as draft, move through review, and only published documents reach entitled viewers (including the customer portal). If you need a scratch pad, use something else. If you need a record that survives audits and bad days, use SoldiDMS.`,
          tip: 'Open https://dms.soldikeeper.com (fallback: https://dms-soldikeeper.vercel.app). September 2026 build.',
        },
        {
          id: 'ch13-s2',
          title: 'Who Uses It',
          type: 'list',
          items: [
            'Internal operators — upload, edit, classify, run workflows, publish',
            'Reviewers / approvers — accept or reject submissions',
            'Admins — permissions, analytics, audit, legal-hold, manual save',
            'Customers (My Documents portal) — view what you explicitly shared as published',
          ],
        },
        {
          id: 'ch13-s3',
          title: 'Core Concepts in One Pass',
          type: 'list',
          items: [
            'Vault — one of nine fixed buckets (procedures, legal, technical, financial, marketing, contracts, customer, hr, compliance)',
            'Folder — organization inside a vault',
            'Status — draft → review → published → archived',
            'Classification — unclassified | internal | confidential | restricted | secret (security label, not a vault)',
            'Version — every serious save/check-in keeps history',
            'Permission — view | comment | edit | admin on a doc/folder',
            'Workflow — the approval gate between draft and published',
          ],
        },
        {
          id: 'ch13-s4',
          title: 'Sign In',
          type: 'steps',
          steps: [
            'Go to dms.soldikeeper.com',
            'Sign in on the DMS login screen — or accept a handoff link from SoldiKeeper that includes ?code=',
            'If you used a handoff link, wait for the exchange to finish (you should land authenticated without retyping the password)',
            'Confirm you see your vaults / documents home — if not, you are not entitled; stop guessing and ask an admin',
          ],
          tip: 'Handoff codes are short-lived. Do not bookmark a URL that still has ?code= in it.',
        },
        {
          id: 'ch13-s5',
          title: 'Founder Rules of Thumb',
          type: 'list',
          items: [
            'Draft is private work product. Published is a promise.',
            'Wrong vault is worse than slow upload — fix classification early.',
            'If it is confidential or higher, think twice before sharing broadly.',
            'Legal-hold means stop editing. Not "edit carefully."',
            'Customer portal only shows what permissions + published status allow — never assume "they can see it somehow."',
          ],
        },
      ],
    },
    {
      id: 'ch14',
      title: 'Vaults, Folders & Upload',
      sections: [
        {
          id: 'ch14-s1',
          title: 'The Nine Vaults',
          type: 'list',
          items: [
            'procedures — SOPs, runbooks, how we operate',
            'legal — counsel, policies, regulatory text',
            'technical — architecture, engineering specs',
            'financial — budgets, statements, finance packs',
            'marketing — campaigns, brand, public-facing drafts',
            'contracts — countersigned and negotiation copies',
            'customer — materials destined for or about customers',
            'hr — people ops (handle classification carefully)',
            'compliance — audits, controls, evidence',
          ],
          tip: 'These nine names are fixed in the product. Do not invent a tenth vault in conversation or tickets.',
        },
        {
          id: 'ch14-s2',
          title: 'Create a Folder',
          type: 'steps',
          steps: [
            'Open the target vault',
            'Choose New Folder (or equivalent folder action)',
            'Name it for humans who will search six months from now',
            'Nest under an existing folder if the tree already has a clear parent',
            'Save — empty folders are fine; cluttered catch-all folders are not',
          ],
        },
        {
          id: 'ch14-s3',
          title: 'Upload a Document (Always Starts Draft)',
          type: 'steps',
          steps: [
            'Open the vault (and folder) where the file belongs',
            'Click Upload',
            'Select a file — hard limit 50MB; larger files will fail at the API',
            'Confirm vault + folder + initial classification',
            'Submit — status is draft until a workflow publishes it',
            'Open the new document and verify preview before you walk away',
          ],
          tip: 'Uploads land in GridFS on the backend. If upload fails at ~50MB, split or compress — do not ask support to "just raise the limit" without a reason.',
        },
        {
          id: 'ch14-s4',
          title: 'AI Classify Preview',
          type: 'steps',
          steps: [
            'After upload (or from the document view), run AI classify / preview when offered',
            'Read the suggested vault, tags, and confidence — treat it as a strong hint, not gospel',
            'Accept, adjust vault/classification, or dismiss',
            'Save your decision — wrong vault + published is expensive to unwind in front of customers',
          ],
          tip: 'AI can also summarize and answer questions on the document. Use it to speed review, not to skip reading high-classification material.',
        },
        {
          id: 'ch14-s5',
          title: 'Duplicates',
          type: 'body',
          body: `SoldiDMS flags likely duplicates on upload/compare. Do not create a second "final_v7_REAL" because search was hard — fix naming, folders, or versions on the original. If the duplicate warning is wrong, proceed deliberately and leave a comment explaining why. Two published copies of the same contract is how companies lose lawsuits and trust.`,
        },
      ],
    },
    {
      id: 'ch15',
      title: 'Document Lifecycle',
      sections: [
        {
          id: 'ch15-s1',
          title: 'Preview & Open',
          type: 'steps',
          steps: [
            'Find the document in vault browse, search, or My Documents',
            'Open it — preview renders for supported types',
            'Check status (draft|review|published|archived) and classification before sharing a link',
            'If preview fails, download only if your permission allows and the file is not under legal-hold restrictions',
          ],
        },
        {
          id: 'ch15-s2',
          title: 'Versions',
          type: 'steps',
          steps: [
            'Open the document → Versions',
            'Scan the version list (who, when, note)',
            'Open or compare an older version when you need truth, not vibes',
            'Restore only when you intend to make that snapshot current — and tell reviewers in a comment',
          ],
          tip: 'Checkout/checkin exists so two editors do not silently overwrite each other. Check out before a serious edit; check in when done so a version is recorded.',
        },
        {
          id: 'ch15-s3',
          title: 'Comments',
          type: 'steps',
          steps: [
            'Open the document',
            'Add a comment with the decision or question — "lgtm" without context wastes everyone',
            'Reply in-thread when resolving',
            'Comment permission is enough to discuss; edit permission is required to change the file',
          ],
        },
        {
          id: 'ch15-s4',
          title: 'Share & Permissions',
          type: 'steps',
          steps: [
            'Open Share / Permissions on the document (or folder, when managing a tree)',
            'Grant the minimum level: view, comment, edit, or admin',
            'Remove access the day it is no longer needed',
            'Remember: permission without published status still does not put a draft in the customer portal',
          ],
          tip: 'Document admin ≠ DMS platform admin. Audit, analytics, compliance tools stay behind the stricter admin gate.',
        },
        {
          id: 'ch15-s5',
          title: 'Quality & Related Documents',
          type: 'list',
          items: [
            'Quality — fill required metadata, fix broken previews, keep classification honest',
            'Related — link contracts to amendments, procedures to runbooks, customer packs to source specs',
            'If quality checks fail, fix before submitting for publish — reviewers are not your spellchecker of record',
          ],
        },
      ],
    },
    {
      id: 'ch16',
      title: 'Approvals & Publishing',
      sections: [
        {
          id: 'ch16-s1',
          title: 'Why Workflows Exist',
          type: 'body',
          body: `Publishing is a commitment. Workflows force a second brain between draft and published. Skip that culture and you will ship the wrong PDF to a customer on a Friday night. Submit → review → approve or reject. That is the product. Use it.`,
        },
        {
          id: 'ch16-s2',
          title: 'Submit for Review',
          type: 'steps',
          steps: [
            'Finish editing; check in if you had the document checked out',
            'Confirm vault, classification, and preview look right',
            'Start / submit the workflow — status moves to review',
            'Notify reviewers if the workflow does not already ping them',
            'Stop editing as if it were still your private draft — further changes may reset or confuse the review',
          ],
        },
        {
          id: 'ch16-s3',
          title: 'Approve',
          type: 'steps',
          steps: [
            'Open the document in review',
            'Read it. Actually read it.',
            'Approve through the workflow action',
            'Status becomes published — entitled viewers (and customer portal, when shared) can see it',
          ],
          tip: 'If you are not sure, reject or comment. Silence is not approval.',
        },
        {
          id: 'ch16-s4',
          title: 'Reject',
          type: 'steps',
          steps: [
            'Open the document in review',
            'Reject with a clear reason in comments (what to fix, not just "no")',
            'Author returns to draft (or workflow-defined state), fixes, resubmits',
          ],
        },
        {
          id: 'ch16-s5',
          title: 'After Publish',
          type: 'list',
          items: [
            'Archived — retire without deleting history when the doc is obsolete',
            'Legal-hold — admins can freeze mutations; do not try to "quickly edit anyway"',
            'New major change — check out, edit, new version, new workflow submit — do not silently overwrite a published promise',
          ],
        },
      ],
    },
    {
      id: 'ch17',
      title: 'Document Editor & Templates',
      sections: [
        {
          id: 'ch17-s1',
          title: 'When to Use the Editor',
          type: 'body',
          body: `Use the built-in editor for living documents you expect to version inside SoldiDMS — procedures, internal notes, structured packs. Upload binary PDFs when the source of truth is external (signed contracts, bank PDFs). Do not paste a signed contract into the editor and call it original.`,
        },
        {
          id: 'ch17-s2',
          title: 'Edit Safely (Checkout → Edit → Save → Checkin)',
          type: 'steps',
          steps: [
            'Open the document and check out (exclusive edit lock)',
            'Edit in the document editor',
            'Save drafts as you go (editor/save path)',
            'Check in when finished so a DmsDocumentVersion is recorded',
            'If you abandon the edit, release the checkout — do not leave zombies locks for your teammates',
          ],
          tip: 'Legal-hold and insufficient permission will block edits. That is correct behavior.',
        },
        {
          id: 'ch17-s3',
          title: 'Templates',
          type: 'steps',
          steps: [
            'Choose New from template (or Templates library)',
            'Pick the template that matches the vault purpose',
            'Fill required fields — templates exist to stop blank-page chaos',
            'Save as draft in the correct vault/folder',
            'Submit workflow when ready to publish',
          ],
        },
        {
          id: 'ch17-s4',
          title: 'Manuals (Editors / Admins)',
          type: 'body',
          body: `User and technical manuals live under /manual and /tech-manual — AdminRoute, editorOnly. Defaults ship in manualContent.js / technicalManualContent.js; localStorage holds local overrides while you edit. Durable company-wide save is POST /api/dms/manual/save and requires DMS admin. If you are not supposed to edit the bible, you will not see the editor. Good.`,
        },
      ],
    },
    {
      id: 'ch18',
      title: 'My Documents (Customer Portal) & Notifications',
      sections: [
        {
          id: 'ch18-s1',
          title: 'My Documents — Customer Portal',
          type: 'body',
          body: `My Documents is the customer-facing slice of SoldiDMS. Customers see published documents they are permitted to view — not your draft pile, not every vault, not "whatever is in marketing." If a customer cannot see a file, check status (must be published) and permissions before blaming the portal.`,
        },
        {
          id: 'ch18-s2',
          title: 'Customer: Find & Open',
          type: 'steps',
          steps: [
            'Sign in to the DMS / portal experience you were given',
            'Open My Documents',
            'Browse or search the list',
            'Open a document to preview or download per your access',
            'Use comments only if you were granted comment (many customer shares are view-only)',
          ],
        },
        {
          id: 'ch18-s3',
          title: 'Operator: Share Into the Portal',
          type: 'steps',
          steps: [
            'Ensure the document is published (workflow approved)',
            'Grant the customer (or customer role) at least view',
            'Confirm classification is appropriate for external eyes',
            'Tell the customer once — do not spam five "please see attached" channels for the same file',
            'Revoke when the engagement ends',
          ],
          tip: 'Sharing a draft "just for a quick look" outside workflow is how confidential material leaks. Publish on purpose.',
        },
        {
          id: 'ch18-s4',
          title: 'Notifications',
          type: 'list',
          items: [
            'Review requested — someone submitted a workflow that needs you',
            'Approved / rejected — your submission moved',
            'Shared with you — new permission on a document',
            'Comments — people are talking on a doc you follow or own',
            'System / compliance — legal-hold and admin notices; read these',
          ],
        },
        {
          id: 'ch18-s5',
          title: 'Stay On Top of Notifications',
          type: 'steps',
          steps: [
            'Open the DMS notification center regularly during active deals or audits',
            'Act on review requests same day when you are the blocker',
            'Archive or mark read only after you handled the underlying item',
            'If you get zero notifications and own approvals, your permissions or workflow routing are wrong — fix routing, do not ignore silence',
          ],
          tip: 'September 2026: SoldiDMS notifications are in-product (DmsNotification). Do not assume email caught it unless your tenant explicitly configured mail outs.',
        },
      ],
    },
    {
      id: 'appendixA',
      title: 'Appendix A: Keyboard Shortcuts',
      sections: [
        {
          id: 'appA-s1',
          title: 'Desktop Keyboard Shortcuts',
          type: 'list',
          items: [
            'Alt + T — Open add transaction dialog',
            'Alt + B — Navigate to Budgets',
            'Alt + R — Navigate to Receipts',
            'Alt + I — Navigate to AI Insights',
            'Ctrl + F — Focus the search bar',
            'Escape — Close any open dialog or drawer',
          ],
        },
      ],
    },
    {
      id: 'appendixB',
      title: 'Appendix B: Troubleshooting',
      sections: [
        {
          id: 'appB-s1',
          title: 'Login & Account Access',
          body: 'Most login problems are caused by unconfirmed email addresses, incorrect credentials, or an expired session. Work through the steps below in order before contacting support.',
          items: [
            'Cannot log in — "Invalid credentials": Double-check your email address for typos. Passwords are case-sensitive. Use "Forgot Password" to reset via email link.',
            'Cannot log in — "Email not verified": Check your inbox and spam for the verification email. Click "Resend Verification" on the login screen if needed. The link expires after 24 hours.',
            'Logged out unexpectedly: Sessions expire after 7 days of inactivity. This is a security feature. Log back in normally — your data is preserved.',
            'Two-factor / Google sign-in fails: Ensure pop-ups are allowed for soldikeeper.com in your browser settings. Disable VPNs or ad-blockers temporarily and retry.',
            'Account locked after too many attempts: Wait 15 minutes before trying again. Use "Forgot Password" to regain access immediately.',
            '"Account not found" after switching devices: Confirm you are using the same email address and sign-in method (email/password vs. Google) as the original registration.',
          ],
          tip: 'If you share a computer, always log out from Settings > Account before closing the browser to prevent session conflicts.',
        },
        {
          id: 'appB-s2',
          title: 'Transactions & Categories',
          body: 'Issues with transactions are almost always due to wrong categories, date ranges, or filters being active. Check these before assuming data is missing.',
          items: [
            'Transaction does not appear in the list: Check that no search filter or date filter is active (look for the blue filter badge in the toolbar). Expand the date range or clear all filters.',
            'Transaction added but not visible in a budget: The transaction category must exactly match the budget category name. Edit the transaction and re-select the correct category.',
            'Amount shows in wrong currency: Currency is set per-account in Settings > Preferences. Verify the account currency matches your transaction currency.',
            'Duplicate transaction appears: Manual + CSV import can cause duplicates. Delete one copy. To prevent in future, check "Skip duplicates" when importing CSV files.',
            'Cannot delete a transaction (greyed out): Transactions linked to a settled SplitBill cannot be deleted until the bill is archived. Go to SplitSmart and archive the bill first.',
            'Recurring transaction did not generate: Recurring templates run at midnight on the scheduled date in your local timezone. If the date just passed, check Settings > Recurring and manually trigger it.',
          ],
          warning: 'Deleting a transaction is permanent and immediately affects all budget totals for that period. Export your data first if you are unsure.',
        },
        {
          id: 'appB-s3',
          title: 'Budgets & Reports',
          body: 'Budget discrepancies are almost always explained by category mismatches, date boundaries, or the rollover setting. Use these steps to diagnose.',
          items: [
            'Budget total looks wrong: Open the budget detail view and check the transaction list inside it. Verify all shown transactions belong to the correct category and period.',
            'Spent amount is higher than expected: A recently edited transaction may have had its category changed. Check Recent Activity in the Dashboard for category edits.',
            'Rollover amount is zero even though I had leftover funds: Rollover must be explicitly enabled per budget in Budget Settings. Toggle "Enable Rollover" and it applies from the next cycle onward — not retroactively.',
            'Budget shows 100% used but I still have money: Check if there are pending recurring transactions scheduled for this period that have already been counted.',
            'Monthly report shows no data: Reports require at least one complete calendar month of transactions. Partial months show partial data.',
            'Exported CSV is empty: Ensure the date range in the export dialog includes the period you want. The default export range is "current month".',
          ],
          note: 'Rollover only carries unused budget forward — it does not reduce the next period\'s base limit. The rollover amount appears as a separate line in the budget detail.',
        },
        {
          id: 'appB-s4',
          title: 'Receipt Scanning & OCR',
          body: 'OCR accuracy depends heavily on image quality. Before reporting a scan failure, try the improvements below — they resolve 95% of scanning issues.',
          steps: [
            'Ensure the receipt is on a flat, dark, non-reflective surface with no creases or folds.',
            'Take the photo in good natural or artificial light — avoid flash glare directly on the paper.',
            'Hold the camera parallel to the receipt (not at an angle) and make sure all four corners are visible in the frame.',
            'For very long receipts, try the "Upload PDF" option instead of camera capture — digital PDF receipts from online stores give the most accurate results.',
            'After scanning, always review the extracted fields before saving. Correct the merchant name, amount, date, and category if needed.',
            'If a field is consistently misread (e.g., "0" vs "O"), manually correct it after extraction — the AI will learn your corrections over time.',
          ],
          tip: 'PDF receipts from online stores (Amazon, PayPal, utilities) consistently yield 98%+ accuracy. Crumpled thermal paper receipts are the most error-prone — flatten and photograph under bright light.',
        },
        {
          id: 'appB-s5',
          title: 'Subscriptions & Billing',
          body: 'Questions about your SoldiKeeper plan, trial, or billing are handled here. For disputes with tracked subscriptions (your own services), see the Subscriptions chapter.',
          items: [
            'Trial expired but I did not receive a warning: Trial expiry emails are sent 3 days and 1 day before expiry. Check your spam folder. The in-app banner also appears 7 days before.',
            'Upgraded to Premium but features are still locked: Log out and log back in to refresh your subscription token. If still locked after 5 minutes, contact support with your payment confirmation.',
            'Downgraded plan but still being charged: Downgrade takes effect at the end of the current billing period. You retain Premium access until then and will not be charged again.',
            'Cannot cancel subscription: Go to Settings > Subscription > Manage Plan > Cancel. If the Cancel button is missing, your subscription was purchased via Google Play — cancel through the Play Store app.',
            'A tracked subscription shows wrong renewal date: Edit the subscription and update the renewal date manually. SoldiKeeper does not connect to banks to verify — it relies on what you entered.',
            'Coupon or promo code not applied: Promo codes must be entered at checkout, not after. Contact support within 48 hours of purchase with your receipt and the code for a manual adjustment.',
          ],
          note: 'SoldiKeeper billing is handled by Stripe. Your full card number is never stored on our servers. For billing disputes, email billing@soldikeeper.com with your order ID.',
        },
        {
          id: 'appB-s6',
          title: 'AI Insights & Smart Features',
          body: 'The AI engine requires a minimum data history to generate meaningful insights. If insights seem missing or inaccurate, these are the most common reasons.',
          items: [
            'No insights appear at all: The AI requires at least 30 days of transaction history and a minimum of 10 transactions to generate patterns. Continue adding transactions — insights activate automatically.',
            'Insights are outdated or repetitive: Insights refresh every 24 hours. Pull down to refresh on mobile, or click the refresh icon in the AI Insights panel on desktop.',
            'Anomaly alert for a transaction I made intentionally: Dismiss the alert using the "Understood" button. Dismissed anomalies are not shown again and help the model calibrate your spending patterns.',
            'Savings suggestion is not relevant: Tap "Not relevant" on the suggestion card. The model uses your feedback to improve future recommendations.',
            'AI category suggestion is wrong: Override it by selecting the correct category manually. After 3–5 corrections of the same merchant, the AI will learn the correct category for that merchant.',
            'Smart search returned no results: Smart search understands natural language but works best with specific terms (e.g., "grocery spending in January" rather than "food last month"). Try rephrasing.',
          ],
          tip: 'The AI becomes significantly more accurate after 3 months of data. The first month of insights is directional — use them as guidance, not precise accounting.',
        },
        {
          id: 'appB-s7',
          title: 'App Performance & Connectivity',
          body: 'Slow performance, blank screens, or connection errors are usually temporary. These steps resolve the vast majority of performance issues.',
          steps: [
            'Hard-refresh the page: Ctrl+Shift+R on Windows/Linux, Cmd+Shift+R on Mac. This bypasses cached files that may be outdated after an app update.',
            'Clear browser storage: Open DevTools (F12) > Application > Storage > Clear site data. This fixes "Loading chunk failed" or blank-screen errors after updates.',
            'Check your internet connection: SoldiKeeper requires an active connection. Offline mode is not currently supported on the web version.',
            'Try a different browser: Chrome and Firefox are fully supported. Safari may have minor display differences. If the issue is browser-specific, use Chrome.',
            'Disable browser extensions: Ad-blockers or privacy extensions can interfere with API calls. Try in Incognito mode (which disables most extensions) to confirm.',
            'Check https://status.soldikeeper.com for any active incidents before raising a support ticket.',
          ],
          warning: 'If you see "ChunkLoadError" or "Loading chunk failed" in the browser console, always do a hard refresh first (Ctrl+Shift+R). This resolves the error in 100% of cases — it is a caching artefact, not a bug.',
        },
        {
          id: 'appB-s8',
          title: 'Mobile App Issues',
          body: 'The SoldiKeeper Android app shares the same backend as the web app. Account data is fully synced in real time across both platforms.',
          items: [
            'App crashes on launch: Force-stop the app (device Settings > Apps > SoldiKeeper > Force Stop), then reopen. If the crash persists, uninstall and reinstall from the Play Store.',
            'Camera permission denied for receipt scan: Go to device Settings > Apps > SoldiKeeper > Permissions > Camera and enable it. The app cannot request this permission a second time automatically.',
            'Data not syncing between mobile and web: Pull down to force a sync refresh. If data is still missing, check your internet connection — mobile data and Wi-Fi both work.',
            'Notifications not arriving: Ensure notifications are enabled in device Settings > Apps > SoldiKeeper > Notifications. Also check that battery optimization is not killing background processes for SoldiKeeper.',
            'App is slow or laggy: Free up device storage (the app needs at least 50 MB free) and close background apps. Restart the device if the lag persists after these steps.',
            'Installed update but app looks the same: Force-stop the app and reopen. If still unchanged, uninstall and reinstall from the Play Store to get the latest build.',
          ],
          note: 'The mobile app auto-updates via the Play Store when connected to Wi-Fi. Enable auto-updates in Play Store > Settings > Auto-update apps to always have the latest version.',
        },
      ],
    },
  ],
};

export default defaultManualContent;
